/**
 * Notification Context
 *
 * Provides notification state and operations to the component tree.
 * Features:
 * - Automatic polling for new notifications (30s interval)
 * - Portal-aware recipient type (user vs tech)
 * - Smart grouping for display
 * - Mark as read operations
 * - WebSocket-ready architecture (polling now, WebSocket later)
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { usePortal } from './PortalContext';
import { useNotificationRepository } from '../data/hooks';
import type {
  Notification,
  NotificationGroup,
} from '../types/notification';
import { RecipientType } from '../types/notification';
import { UserRole } from '../types/ticket';

// ============================================
// Types
// ============================================

export interface NotificationContextValue {
  /** All notifications for current user */
  notifications: Notification[];

  /** Grouped notifications for display */
  groupedNotifications: NotificationGroup[];

  /** Number of unread notifications */
  unreadCount: number;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: string | null;

  /** Mark a single notification as read */
  markAsRead: (id: string) => Promise<void>;

  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;

  /** Delete a notification */
  deleteNotification: (id: string) => Promise<void>;

  /** Manually refresh notifications */
  refresh: () => Promise<void>;

  /** Last refresh timestamp */
  lastRefreshed: Date | null;
}

interface NotificationProviderProps {
  children: ReactNode;
  /** Polling interval in milliseconds (default: 30000) */
  pollingInterval?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_POLLING_INTERVAL = 30000; // 30 seconds
const MAX_GROUPED_NOTIFICATIONS = 50;

// ============================================
// Context
// ============================================

const NotificationContext = createContext<NotificationContextValue | null>(null);

// ============================================
// Helper Functions
// ============================================

/**
 * Determine recipient type based on portal and user role
 */
function getRecipientType(activePortal: 'user' | 'it', userRole: UserRole): RecipientType {
  // If in IT portal and user has IT role, treat as tech
  if (activePortal === 'it') {
    const itRoles = [UserRole.ADMIN, UserRole.T1_TECH, UserRole.T2_TECH];
    if (itRoles.includes(userRole)) {
      return RecipientType.TECH;
    }
  }

  // Default to user recipient type
  return RecipientType.USER;
}

// ============================================
// Provider Component
// ============================================

export function NotificationProvider({
  children,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
}: NotificationProviderProps) {
  const currentUser = useCurrentUser();
  const { activePortal } = usePortal();
  const notificationRepo = useNotificationRepository();

  // State
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [groupedNotifications, setGroupedNotifications] = useState<NotificationGroup[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // Refs for polling
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  // Determine recipient type based on current portal
  const recipientType = useMemo(
    () => getRecipientType(activePortal, currentUser.role),
    [activePortal, currentUser.role]
  );

  /**
   * Fetch all notifications for current user
   */
  const fetchNotifications = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const [allNotifications, grouped, count] = await Promise.all([
        notificationRepo.getByRecipient(currentUser.id, recipientType),
        notificationRepo.getGrouped(currentUser.id, recipientType, {
          limit: MAX_GROUPED_NOTIFICATIONS,
          includeRead: true,
        }),
        notificationRepo.getUnreadCount(currentUser.id, recipientType),
      ]);

      if (isMountedRef.current) {
        setNotifications(allNotifications);
        setGroupedNotifications(grouped);
        setUnreadCount(count);
        setLastRefreshed(new Date());
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      if (isMountedRef.current) {
        setError('Failed to load notifications');
      }
    }
  }, [notificationRepo, currentUser.id, recipientType]);

  /**
   * Initial load
   */
  useEffect(() => {
    isMountedRef.current = true;

    async function initialLoad() {
      setIsLoading(true);
      await fetchNotifications();
      setIsLoading(false);
    }

    initialLoad();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchNotifications]);

  /**
   * Set up polling
   */
  useEffect(() => {
    // Clear existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    // Start new polling interval
    pollingRef.current = setInterval(() => {
      fetchNotifications();
    }, pollingInterval);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchNotifications, pollingInterval]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(
    async (id: string) => {
      try {
        const updated = await notificationRepo.markAsRead(id);
        if (updated && isMountedRef.current) {
          // Optimistic update
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date() } : n))
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));

          // Update grouped notifications
          setGroupedNotifications((prev) =>
            prev.map((group) => ({
              ...group,
              notifications: group.notifications.map((n) =>
                n.id === id ? { ...n, isRead: true, readAt: new Date() } : n
              ),
              hasUnread: group.notifications.some((n) => n.id !== id && !n.isRead),
              primaryNotification:
                group.primaryNotification.id === id
                  ? { ...group.primaryNotification, isRead: true, readAt: new Date() }
                  : group.primaryNotification,
            }))
          );
        }
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    },
    [notificationRepo]
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationRepo.markAllAsRead(currentUser.id, recipientType);
      if (isMountedRef.current) {
        const now = new Date();
        // Optimistic update
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: now })));
        setUnreadCount(0);

        // Update grouped notifications
        setGroupedNotifications((prev) =>
          prev.map((group) => ({
            ...group,
            notifications: group.notifications.map((n) => ({
              ...n,
              isRead: true,
              readAt: now,
            })),
            hasUnread: false,
            primaryNotification: {
              ...group.primaryNotification,
              isRead: true,
              readAt: now,
            },
          }))
        );
      }
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, [notificationRepo, currentUser.id, recipientType]);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        const notification = notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.isRead;

        const success = await notificationRepo.delete(id);
        if (success && isMountedRef.current) {
          // Remove from state
          setNotifications((prev) => prev.filter((n) => n.id !== id));

          // Update unread count if deleted notification was unread
          if (wasUnread) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }

          // Update grouped notifications
          setGroupedNotifications((prev) =>
            prev
              .map((group) => {
                const filteredNotifications = group.notifications.filter((n) => n.id !== id);
                if (filteredNotifications.length === 0) {
                  return null; // Remove empty group
                }
                return {
                  ...group,
                  notifications: filteredNotifications,
                  count: filteredNotifications.length,
                  hasUnread: filteredNotifications.some((n) => !n.isRead),
                  primaryNotification:
                    group.primaryNotification.id === id
                      ? filteredNotifications[0]
                      : group.primaryNotification,
                };
              })
              .filter((group): group is NotificationGroup => group !== null)
          );
        }
      } catch (err) {
        console.error('Failed to delete notification:', err);
      }
    },
    [notificationRepo, notifications]
  );

  /**
   * Manual refresh
   */
  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchNotifications();
    setIsLoading(false);
  }, [fetchNotifications]);

  // Context value
  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      groupedNotifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      refresh,
      lastRefreshed,
    }),
    [
      notifications,
      groupedNotifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      refresh,
      lastRefreshed,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access notification context
 * @throws Error if used outside of NotificationProvider
 */
export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }

  return context;
}

/**
 * Hook to get just the unread count
 * Safe to use outside of NotificationProvider (returns 0)
 */
export function useUnreadCount(): number {
  const context = useContext(NotificationContext);
  return context?.unreadCount ?? 0;
}
